import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Pencil, CheckCircle2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  PLACE_TYPE_LABELS, AMENITY_LABELS,
  computeAllIn,
} from '../../lib/listingConstants';

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-1.5">
    <span className="text-xs text-slate-500">{label}</span>
    <span className="text-xs text-slate-900 font-medium text-right min-w-0 break-words">{value}</span>
  </div>
);

const Section = ({ title, editStep, goTo, children }) => (
  <Card className="bg-white border-slate-200">
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-heading font-bold text-slate-900">{title}</h2>
        <button
          type="button"
          onClick={() => goTo(editStep)}
          className="inline-flex items-center text-xs font-medium text-amber-700 hover:text-amber-900"
          data-testid={`edit-${editStep}`}
        >
          <Pencil className="h-3 w-3 mr-1" /> Edit
        </button>
      </div>
      <div className="divide-y divide-slate-100">{children}</div>
    </CardContent>
  </Card>
);

const StepReview = ({ listing, goTo }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [missing, setMissing] = useState([]);

  const l = listing;
  const total = computeAllIn(l);

  const submit = async () => {
    setSubmitting(true);
    setMissing([]);
    try {
      await api.post(`/listings/draft/${l.listing_id}/submit`);
      navigate(`/listings/new/${l.listing_id}/submitted`, { replace: true });
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === 'object' && detail?.missing) {
        setMissing(detail.missing);
        toast.error('A few things are still missing. See the list below.');
      } else {
        toast.error(typeof detail === 'string' ? detail : "Couldn't submit — try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-heading font-extrabold text-slate-900">Review &amp; submit</h1>
        <p className="mt-1 text-sm text-slate-600">Check each section. Once you submit, our team reviews within 24 hours.</p>
      </div>

      {missing.length > 0 && (
        <Card className="bg-rose-50 border-rose-200" data-testid="review-missing">
          <CardContent className="p-4">
            <div className="flex gap-2 items-start">
              <AlertCircle className="h-4 w-4 text-rose-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-rose-900">Still needed:</p>
                <ul className="mt-1 text-xs text-rose-800 list-disc ml-4 space-y-0.5">
                  {missing.map((m) => <li key={m}>{m}</li>)}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Section title="Property" editStep="property-type" goTo={goTo}>
        <Row label="Type" value={PLACE_TYPE_LABELS[l.place_type] || '—'} />
        <Row label="Furnished" value={l.furnished || '—'} />
        <Row label="Bedrooms / bathrooms" value={`${l.bedroom_count ?? '—'} bed · ${l.bathroom_count ?? '—'} ${l.bathroom_type || ''} bath`} />
        {l.shared_with_others && <Row label="Shared with" value={`${l.shared_with_count ?? 0} others`} />}
      </Section>

      <Section title="Location" editStep="location" goTo={goTo}>
        <Row label="Address" value={l.address_line_1 || '—'} />
        <Row label="City" value={`${l.city || '—'}${l.neighborhood ? `, ${l.neighborhood}` : ''}`} />
        <Row label="Postal" value={l.postal_code || '—'} />
      </Section>

      <Section title="Photos" editStep="photos" goTo={goTo}>
        <Row label="Uploaded" value={`${(l.photos || []).length} photo${(l.photos || []).length === 1 ? '' : 's'}`} />
      </Section>

      <Section title="Pricing (all-in)" editStep="pricing" goTo={goTo}>
        <Row label="All-in total" value={<span className="text-base font-heading font-bold text-amber-700">${total.toLocaleString()}/mo</span>} />
        <Row label="Rent" value={`$${(l.monthly_rent_cad ?? 0).toLocaleString()}`} />
        <Row label="Utilities" value={l.includes_utilities ? `Included (${(l.utilities_included || []).join(', ') || 'all'})` : `+$${l.additional_utility_cost_cad ?? 0}`} />
        <Row label="Internet" value={l.includes_internet ? 'Included' : `+$${l.internet_cost_if_separate_cad ?? 0}`} />
        <Row label="Parking" value={l.includes_parking ? 'Included' : (l.parking_type === 'none' ? 'None' : `+$${l.parking_cost_if_separate_cad ?? 0}`)} />
        <Row label="Security deposit" value={`$${l.security_deposit_cad ?? 0}`} />
      </Section>

      <Section title="Lease terms" editStep="lease" goTo={goTo}>
        <Row label="Type" value={l.lease_type?.replace('_', '-') || '—'} />
        <Row label="Length" value={l.lease_length_months ? `${l.lease_length_months} months` : '—'} />
        <Row label="Earliest move-in" value={l.earliest_move_in_date || '—'} />
        <Row label="Ontario Standard Lease" value={l.ontario_standard_lease_will_be_used ? 'Yes' : 'No'} />
      </Section>

      <Section title="Amenities" editStep="amenities" goTo={goTo}>
        <div className="py-2 flex flex-wrap gap-1.5">
          {(l.amenities || []).slice(0, 12).map((a) => (
            <span key={a} className="px-2 py-0.5 text-[11px] bg-slate-100 text-slate-700 rounded-full">
              {AMENITY_LABELS[a] || a}
            </span>
          ))}
          {(l.amenities || []).length > 12 && (
            <span className="px-2 py-0.5 text-[11px] bg-slate-100 text-slate-700 rounded-full">
              +{l.amenities.length - 12} more
            </span>
          )}
          {(!l.amenities || l.amenities.length === 0) && (
            <span className="text-xs text-slate-500">None selected</span>
          )}
        </div>
      </Section>

      <Section title="House rules" editStep="rules" goTo={goTo}>
        <Row label="Pets" value={l.pets_allowed || '—'} />
        <Row label="Smoking" value={l.smoking_allowed || '—'} />
        <Row label="Guests" value={l.guests_allowed || '—'} />
      </Section>

      <Section title="Description" editStep="description" goTo={goTo}>
        <Row label="Title" value={l.title || '—'} />
        <Row label="Top features" value={`${(l.top_features || []).length} added`} />
      </Section>

      <Card className="bg-amber-50 border-amber-200/60">
        <CardContent className="p-4 flex gap-2">
          <CheckCircle2 className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-900 leading-relaxed">
            By submitting, you confirm everything here is accurate. We'll email you when our team
            finishes reviewing (usually within 24 hours).
          </p>
        </CardContent>
      </Card>

      <Button
        className="w-full btn-primary h-12 text-base"
        onClick={submit}
        disabled={submitting}
        data-testid="submit-for-review-btn"
      >
        {submitting ? 'Submitting…' : 'Submit for review'}
      </Button>
    </div>
  );
};

export default StepReview;
